// Thinking indicator with phased labels driven by SSE status frames.
//
// Replaces the previous imperative implementation (streamStatusRef +
// attachStreamStatus + statusLabel + cfThinkPulse keyframes injected
// at app mount). Same UX, but as a real React component so the phase
// is part of normal state instead of being threaded through DOM refs.
//
// Phase enum (mapped from server SSE frames in the parent App):
//   thinking   default while we wait — first paint, no frames yet
//   searching  multi-query / kb / web_search starting
//   reading    fetch_url tool — toolDomain prop carries the hostname
//   tool_done  any tool returned (between using_tool and the next
//              status / first content chunk)
//   composing  upstream connecting / model assembling its reply
//
// CSS lives next to the component as injected styles so the rest of the
// app (no global stylesheet pipeline) doesn't need plumbing changes.
// Injected once per page via a stable `<style id>` so HMR / re-mounts
// don't stack duplicates.

import { memo } from "react";

const STYLE_ID = "thinking-indicator-style";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = `
.thinking-indicator { display: flex; align-items: center; gap: 8px; padding: 8px 12px; opacity: 0.85; font-size: 0.9em; color: var(--cf-thinking-fg, #888); }
.thinking-indicator .dots { display: inline-flex; gap: 3px; }
.thinking-indicator .dots span { width: 6px; height: 6px; border-radius: 50%; background: currentColor; opacity: 0.4; display: inline-block; animation: thinking-pulse 1.4s infinite ease-in-out; }
.thinking-indicator .dots span:nth-child(2) { animation-delay: 0.2s; }
.thinking-indicator .dots span:nth-child(3) { animation-delay: 0.4s; }
.thinking-indicator .label { user-select: none; }
@keyframes thinking-pulse { 0%, 80%, 100% { opacity: 0.4; transform: scale(1); } 40% { opacity: 1; transform: scale(1.15); } }
`;
  document.head.appendChild(s);
}

function labelFor(phase, toolDomain) {
  switch (phase) {
    case "searching": return "Searching the knowledge base\u2026";
    case "reading":   return toolDomain ? `Reading ${toolDomain}\u2026` : "Reading the web\u2026";
    case "tool_done": return "Reviewing results\u2026";
    case "composing": return "Composing\u2026";
    case "thinking":
    default:          return "Thinking\u2026";
  }
}

export const ThinkingIndicator = memo(function ThinkingIndicator({ phase, toolDomain, color }) {
  const label = labelFor(phase, toolDomain);
  // `color` is an optional theme override so the dots track the chat-bubble
  // muted color in dark/light modes. Falls back to the CSS variable, which
  // itself falls back to a neutral gray.
  const style = color ? { color } : undefined;
  return (
    <div className="thinking-indicator" role="status" aria-live="polite" style={style}>
      <span className="dots"><span/><span/><span/></span>
      <span className="label">{label}</span>
    </div>
  );
});

export default ThinkingIndicator;
